import { toTitleCase } from '@helpers/utils/formatUtils';
import { SelectOption } from '@theme/theme';
import UserStore from '../../services/Session/store';
import { UserType } from '../../services/analytics';
import type { History } from 'history';
import { BBSB_SPORTS, VOLLEYBALL } from '@constants/strings';

export const getTeamAgeType = (age: number | string, level: string): string => {
  let result = `${age}U`;

  if (age === 0 || age === -1 || age === '') {
    result = level ? toTitleCase(level) : '';
  } else if (age === 99) {
    result = 'Adult League';
  }

  return result;
};

export const generateSeasonOptions = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0 = January, 11 = December

  const seasons = ['Spring', 'Summer', 'Fall', 'Winter'];

  // Get the current season index based on the month
  const currentSeasonIndex = getSeasonIndex(currentMonth);

  const seasonOptions = [];

  // Start with the current season and move to the next three seasons
  let year = currentYear;
  for (let i = 0; i < 4; i++) {
    const seasonIndex = (currentSeasonIndex + i) % 4; // Wrap through the seasons

    // Adjust the year:
    // Winter belongs to the year it starts in (December), so decrement the year for "Winter" if it's not January/February
    if (seasonIndex === 3 && currentSeasonIndex + i < 4) {
      year -= 1; // Winter belongs to the previous year
    } else if (seasonIndex === 0 && i > 0) {
      year += 1; // Increment the year when transitioning from Winter to Spring
    }

    seasonOptions.push({
      label: `${seasons[seasonIndex]} ${year}`,
      value: Number(`${year}${seasonIndex + 1}`), // Season index + 1 for value
    });
  }

  return seasonOptions;
};

export const getSeasonIndex = (month: number): number => {
  if (month >= 2 && month <= 4) {
    return 0; // Spring: March (2), April (3), May (4)
  } else if (month >= 5 && month <= 7) {
    return 1; // Summer: June (5), July (6), August (7)
  } else if (month >= 8 && month <= 10) {
    return 2; // Fall: September (8), October (9), November (10)
  } else {
    return 3; // Winter: December (11), January (0), February (1)
  }
};

export const getNextSeasonLabel = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const seasons = ['Spring', 'Summer', 'Fall', 'Winter'];

  // Get the current season index based on the month using switch
  const currentSeasonIndex = getSeasonIndex(currentMonth);

  // Calculate the next season index and year
  const nextSeasonIndex = (currentSeasonIndex + 1) % 4;
  const nextSeasonYear = nextSeasonIndex === 0 ? currentYear + 1 : currentYear;

  return `${seasons[nextSeasonIndex]} ${nextSeasonYear}`;
};

export const getLabelForSeason = (seasonValue: number) => {
  // Convert seasonValue to a string for easier manipulation
  const seasonString = String(seasonValue);

  // Check if the format matches the expected length of 5 characters
  if (seasonString.length !== 5) {
    return `Invalid Season (${seasonValue})`; // Graceful handling for invalid length
  }

  const year = Math.floor(seasonValue / 10); // Extract year
  let seasonIndex = seasonValue % 10; // Extract season index

  // Handle the special case where 0 maps to Spring
  if (seasonIndex === 0) {
    seasonIndex = 1; // Treat as Spring
  } else if (seasonIndex < 1 || seasonIndex > 4) {
    return `Invalid Season (${seasonValue})`; // Handle other invalid season indices
  }

  const seasons = ['Spring', 'Summer', 'Fall', 'Winter'];

  // Adjust seasonIndex by -1 to align with 0-based array indexing
  return `${seasons[seasonIndex - 1]} ${year}`;
};

export const getTeamAgeLevelOptions = (): Array<SelectOption> => {
  return [
    { label: '6U', value: '6U' },
    { label: '7U', value: '7U' },
    { label: '8U', value: '8U' },
    { label: '9U', value: '9U' },
    { label: '10U', value: '10U' },
    { label: '11U', value: '11U' },
    { label: '12U', value: '12U' },
    { label: '13U', value: '13U' },
    { label: '14U', value: '14U' },
    { label: '15U', value: '15U' },
    { label: '16U', value: '16U' },
    { label: '17U', value: '17U' },
    { label: '18U', value: '18U' },
    { label: 'Adult', value: 'Adult' },
    { label: 'College', value: 'College' },
    { label: 'High School', value: 'High School' },
  ];
};

export const getUserTypeRelativeToTeam = (
  teamId: string,
  userStore: UserStore
) => {
  const teamIds = userStore.userTeams.map((userTeam) => userTeam._id);
  const isAdmin = teamIds.includes(teamId);

  let userType;
  if (!userStore.authUser) {
    userType = UserType.LoggedOut;
  } else if (isAdmin) {
    userType = UserType.Admin;
  } else if (userStore.teamFollowerIds.concat(teamIds).includes(teamId)) {
    userType = UserType.CommunityMember;
  } else {
    userType = UserType.LoggedIn;
  }
  return userType;
};

export const teamScoringLinkRedirection = (
  teamId: string,
  history: History
) => {
  const key = localStorage.getItem('scoreDelegationKey');
  if (key) {
    history.replace(`/${teamId}?tab=scoring&key=${key}`);
  } else {
    history.replace(`/${teamId}`);
  }
};

export enum ScoreboardTypeEnum {
  Default = 'default',
  Minimal = 'minimal',
  Other = 'other',
}

export enum ScoreboardPositionEnum {
  Top = 'top',
  Bottom = 'bottom',
  TopLeft = 'top-left',
  TopRight = 'top-right',
  BottomLeft = 'bottom-left',
  BottomRight = 'bottom-right',
}

export enum PlayerCardPositionEnum {
  TopLeft = 'top-left',
  TopRight = 'top-right',
  BottomLeft = 'bottom-left',
  BottomRight = 'bottom-right',
}

export type ScoreboardType = `${ScoreboardTypeEnum}`;
export type ScoreboardPosition = `${ScoreboardPositionEnum}`;
export type PlayerCardPosition = `${PlayerCardPositionEnum}`;

export const getPlayerCardPosition = (
  scoreboardPos: ScoreboardPosition,
  isFlipped: boolean,
  scoreboardType: ScoreboardType
): PlayerCardPosition => {
  const ST = ScoreboardTypeEnum;
  const SP = ScoreboardPositionEnum;
  const PCP = PlayerCardPositionEnum;

  if (scoreboardType === ST.Minimal) {
    if (scoreboardPos === SP.Top) {
      return isFlipped ? PCP.BottomLeft : PCP.BottomRight;
    }
    if (scoreboardPos === SP.Bottom) {
      return isFlipped ? PCP.TopRight : PCP.TopLeft;
    }
  } else {
    if (scoreboardPos === SP.TopRight) {
      return PCP.TopLeft;
    }
    if (scoreboardPos === SP.BottomLeft) {
      return PCP.BottomRight;
    }
    if (scoreboardPos === SP.Top) {
      return isFlipped ? PCP.BottomRight : PCP.BottomLeft;
    }
    if (scoreboardPos === SP.Bottom) {
      return isFlipped ? PCP.TopRight : PCP.TopLeft;
    }
  }

  return PCP.BottomLeft;
};

export const getIsFlippedFromSettings = (
  scoreboardPos: ScoreboardPosition,
  playerCardPos: PlayerCardPosition,
  scoreboardType: ScoreboardType
): boolean => {
  const SP = ScoreboardPositionEnum;
  const PCP = PlayerCardPositionEnum;
  const ST = ScoreboardTypeEnum;

  if (scoreboardType === ST.Minimal) {
    return (
      (scoreboardPos === SP.Top && playerCardPos === PCP.BottomLeft) ||
      (scoreboardPos === SP.Bottom && playerCardPos === PCP.TopRight)
    );
  }

  return (
    (scoreboardPos === SP.Top && playerCardPos === PCP.BottomRight) ||
    (scoreboardPos === SP.Bottom && playerCardPos === PCP.TopRight)
  );
};

export const getImageName = (
  stylingType: ScoreboardType,
  position: ScoreboardPosition,
  flipped: boolean,
  sport: string
) => {
  const ST = ScoreboardTypeEnum;
  const SP = ScoreboardPositionEnum;

  let prefix = '';
  if (BBSB_SPORTS.includes(sport)) {
    prefix = 'bbsb';
  } else if (sport === VOLLEYBALL.display) {
    prefix = 'volleyball';
  } else {
    prefix = '';
  }

  let imageName = '';

  if (stylingType === ST.Minimal) {
    if (position === SP.Top) {
      imageName = flipped ? 'minimal-top-flipped' : 'minimal-top-unflipped';
    } else if (position === SP.Bottom) {
      imageName = flipped
        ? 'minimal-bottom-flipped'
        : 'minimal-bottom-unflipped';
    }
  } else {
    if (position === SP.TopRight) {
      imageName = 'top-corner-right';
    } else if (position === SP.TopLeft) {
      imageName = 'top-left';
    } else if (position === SP.BottomLeft) {
      imageName = 'bottom-corner-left';
    } else if (position === SP.BottomRight) {
      imageName = 'bottom-corner-right';
    } else if (position === SP.Top) {
      imageName = flipped ? 'top-right' : 'top-left';
    } else if (position === SP.Bottom) {
      imageName = flipped ? 'bottom-right' : 'bottom-left';
    }
  }

  return `${prefix}-${imageName}`;
};
